import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  User, Code, Trophy, FolderGit2, Settings, LogOut, 
  Github, Linkedin, Globe, MapPin, Building, GraduationCap,
  Plus, Edit, Trash2, Save, X, CheckCircle, ExternalLink,
  TrendingUp, Target, Zap, Crown, Star, Award, Loader,
  FileText, Briefcase, Eye, ChevronRight, BookOpen, Play, Clock,
  Search, Filter, MapPinIcon, Calendar, DollarSign, ArrowRight,
  AlertCircle, XCircle, Bell, BellRing, Users, MessageSquare, Ticket, Sparkles, Mic, ShieldCheck, Camera
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import ResumeUpgradePopup from '../components/ResumeUpgradePopup';
import ShareButton from '../components/ShareButton';
import { MyHackathonsCard } from '../components/events/MyHackathonsCard';
import { getFrontendUrl } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Premium design system — Swiss · White + Deep Ruby
const colors = {
  primary: '#B91C1C',
  primaryDark: '#7F1D1D',
  primaryLight: '#FEF2F2',
  primarySoft: '#B91C1C12',
  success: '#059669',
  warning: '#D97706',
  danger: '#B91C1C',
  text: '#0A0A0A',
  textSecondary: '#525252',
  textTertiary: '#A3A3A3',
  background: '#FAFAF9',
  surface: '#FFFFFF',
  subtle: '#F5F5F5',
  border: '#E5E5E5',
  accent: '#0A0A0A'
};

const CodeArenaDashboardPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [verifiedTalent, setVerifiedTalent] = useState(null);
  const [stats, setStats] = useState(null);
  const [achievements, setAchievements] = useState([]);
  const [projects, setProjects] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [myCourses, setMyCourses] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [opportunities, setOpportunities] = useState({ jobs: [], challenges: [], hiring_challenges: [], enterprise_challenges: [] });
  const [myApplications, setMyApplications] = useState([]);
  const [myChallenges, setMyChallenges] = useState({ hiring_challenges: [], enterprise_challenges: [] });
  const [opportunitiesLoading, setOpportunitiesLoading] = useState(false);
  // Opportunities filters (type, search, sort)
  const [oppFilters, setOppFilters] = useState({ type: 'all', search: '', sort: 'newest' });
  const [publicEvents, setPublicEvents] = useState([]);
  const [myRegisteredEvents, setMyRegisteredEvents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);
  
  // Edit states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [projectForm, setProjectForm] = useState({
    title: '',
    description: '',
    tech_stack: [],
    github_url: '',
    live_url: ''
  });
  const [newSkill, setNewSkill] = useState('');
  const [newTech, setNewTech] = useState('');
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  const token = localStorage.getItem('codearena_token');

  useEffect(() => {
    if (!token) {
      navigate('/codearena/auth');
      return;
    }
    fetchMyProfile();
  }, [token]);

  const fetchMyProfile = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/me/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(res.data.profile);
      setVerifiedTalent(res.data.verified_talent || null);
      setStats(res.data.stats);
      setAchievements(res.data.achievements || []);
      setProjects(res.data.projects || []);
      setSubmissions(res.data.submissions || []);
      setEditForm(res.data.profile);
      fetchMyCourses();
      fetchNotifications();
      
      const profileData = res.data.profile;
      const hasPremium = profileData?.resume_premium || profileData?.has_premium;
      const popupShown = sessionStorage.getItem('resume_upgrade_popup_shown');
      if (!hasPremium && !popupShown) {
        setTimeout(() => {
          setShowUpgradePopup(true);
          sessionStorage.setItem('resume_upgrade_popup_shown', 'true');
        }, 2000);
      }
    } catch (err) {
      console.error('Failed to fetch profile:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('codearena_token');
        navigate('/codearena/auth');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/codearena/me/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/codearena/me/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification read:', err);
    }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/codearena/me/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (err) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('codearena_token');
    localStorage.removeItem('codearena_student');
    toast.success('Logged out successfully');
    navigate('/codearena/auth');
  };

  const fetchMyCourses = async () => {
    try {
      const res = await axios.get(`${API}/future-skills/my-courses`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyCourses(res.data.courses || []);
    } catch (err) {
      console.error('Failed to fetch courses:', err);
    }
  };

  const fetchOpportunities = async () => {
    setOpportunitiesLoading(true);
    try {
      const jobsRes = await axios.get(`${API}/codearena/job-board`);
      setOpportunities({
        jobs: jobsRes.data.jobs || [],
        challenges: jobsRes.data.competitions || [],
        hiring_challenges: jobsRes.data.hiring_challenges || [],
        enterprise_challenges: jobsRes.data.enterprise_challenges || []
      });
      
      const appsRes = await axios.get(`${API}/codearena/my-applications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyApplications(appsRes.data.applications || []);
      
      // Fetch my challenge registrations
      try {
        const challengesRes = await axios.get(`${API}/codearena/my-challenges`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMyChallenges({
          hiring_challenges: challengesRes.data.hiring_challenges || [],
          enterprise_challenges: challengesRes.data.enterprise_challenges || []
        });
      } catch (err) {
        console.error('Failed to fetch my challenges:', err);
      }

      // Fetch public events
      try {
        const eventsRes = await axios.get(`${API}/public/events?limit=6`);
        setPublicEvents(eventsRes.data.events || []);
      } catch (err) {
        console.error('Failed to fetch events:', err);
      }

      // Fetch student's registered events
      try {
        const myEventsRes = await axios.get(`${API}/codearena/my-events`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const regs = myEventsRes.data.registrations || [];
        const evs = myEventsRes.data.events || [];
        const evMap = Object.fromEntries(evs.map((e) => [e.id, e]));
        const combined = regs.map((r) => ({
          registration_id: r.id,
          event_id: r.event_id,
          event: evMap[r.event_id] || { id: r.event_id, title: r.event_title },
          payment_status: r.payment_status,
          ticket_id: (r.id || '').slice(0, 12).toUpperCase(),
        }));
        setMyRegisteredEvents(combined);
      } catch (err) {
        console.error('Failed to fetch my events:', err);
      }
    } catch (err) {
      console.error('Failed to fetch opportunities:', err);
    } finally {
      setOpportunitiesLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'opportunities' && token) {
      fetchOpportunities();
    }
    if (activeTab === 'purchases' && token) {
      fetchPurchases();
    }
  }, [activeTab, token]);

  const fetchPurchases = async () => {
    setPurchasesLoading(true);
    try {
      const res = await axios.get(`${API}/codearena/me/purchases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPurchases(res.data.purchases || []);
    } catch (err) {
      console.error('Failed to fetch purchases:', err);
    } finally {
      setPurchasesLoading(false);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');
    const orderId = params.get('order_id');
    const aiOrderId = params.get('ai_order_id');
    
    if (tabParam && ['overview', 'opportunities', 'profile', 'projects', 'achievements', 'submissions', 'courses', 'purchases'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
    
    if ((orderId || aiOrderId) && token) {
      const verifyPayment = async () => {
        try {
          if (orderId) {
            await axios.post(`${API}/codearena/resume/verify-premium`, 
              { order_id: orderId },
              { headers: { Authorization: `Bearer ${token}` }}
            );
            toast.success('Payment verified successfully!');
          } else if (aiOrderId) {
            await axios.post(`${API}/codearena/resume/verify-ai-credits`,
              { order_id: aiOrderId },
              { headers: { Authorization: `Bearer ${token}` }}
            );
            toast.success('AI credits added!');
          }
          window.history.replaceState({}, '', '/codearena/dashboard?tab=purchases');
          fetchPurchases();
        } catch (err) {
          console.error('Payment verification failed:', err);
        }
      };
      verifyPayment();
    }
  }, [token]);

  const saveProfile = async () => {
    try {
      const profileData = {
        username: editForm.username,
        bio: editForm.bio,
        skills: editForm.skills || [],
        frameworks: editForm.frameworks || [],
        tools: editForm.tools || [],
        domains: editForm.domains || [],
        github_url: editForm.github_url,
        linkedin_url: editForm.linkedin_url,
        portfolio_url: editForm.portfolio_url,
        resume_url: editForm.resume_url,
        location: editForm.location,
        looking_for: editForm.looking_for
      };
      
      await axios.put(`${API}/codearena/me/profile`, profileData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile updated successfully!');
      setIsEditingProfile(false);
      fetchMyProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    }
  };

  const avatarSrc = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Only JPEG, PNG or WebP images are allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Max 5MB allowed.');
      return;
    }
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/codearena/me/avatar`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Profile photo updated!');
      await fetchMyProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setAvatarUploading(false);
      if (avatarInputRef.current) avatarInputRef.current.value = '';
    }
  };

  const handleAvatarRemove = async () => {
    setAvatarUploading(true);
    try {
      await axios.delete(`${API}/codearena/me/avatar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Profile photo removed');
      await fetchMyProfile();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove photo');
    } finally {
      setAvatarUploading(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !editForm.skills?.includes(newSkill.trim())) {
      setEditForm(prev => ({
        ...prev,
        skills: [...(prev.skills || []), newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setEditForm(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const addProject = async () => {
    if (!projectForm.title || !projectForm.description) {
      toast.error('Title and description are required');
      return;
    }
    try {
      await axios.post(`${API}/codearena/me/projects`, {
        ...projectForm,
        tech_stack: projectForm.tech_stack
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project added successfully!');
      setShowProjectModal(false);
      setProjectForm({ title: '', description: '', tech_stack: [], github_url: '', live_url: '' });
      fetchMyProfile();
    } catch (err) {
      toast.error('Failed to add project');
    }
  };

  const deleteProject = async (projectId) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await axios.delete(`${API}/codearena/me/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project deleted');
      fetchMyProfile();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const getTierInfo = (score) => {
    if (score >= 900) return { name: 'Legendary', color: '#915907', bgColor: '#915907', icon: Crown };
    if (score >= 750) return { name: 'Elite', color: '#7c3aed', bgColor: '#7c3aed', icon: Star };
    if (score >= 500) return { name: 'Expert', color: '#B91C1C', bgColor: '#B91C1C', icon: Zap };
    if (score >= 300) return { name: 'Advanced', color: '#057642', bgColor: '#057642', icon: TrendingUp };
    if (score >= 100) return { name: 'Intermediate', color: '#915907', bgColor: '#915907', icon: Target };
    return { name: 'Beginner', color: '#666666', bgColor: '#666666', icon: User };
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <Loader className="animate-spin" style={{ color: colors.primary }} size={32} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: colors.background }}>
        <div className="text-center bg-white p-8 border" style={{ borderColor: colors.border, borderRadius: '2px' }}>
          <User className="mx-auto text-zinc-300 mb-4" size={48} />
          <p className="text-zinc-500">Session expired. Please login again.</p>
          <Link to="/codearena/auth" className="font-medium mt-4 inline-block" style={{ color: colors.primary }}>
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  const tier = getTierInfo(stats?.total_score || 0);
  const TierIcon = tier.icon;

  const skillOptions = [
    'Python', 'JavaScript', 'TypeScript', 'Java', 'C++', 'C', 'Go', 'Rust', 'Ruby', 'PHP',
    'React', 'Vue.js', 'Angular', 'Node.js', 'Django', 'Flask', 'FastAPI', 'Spring Boot',
    'MongoDB', 'PostgreSQL', 'MySQL', 'Redis', 'Docker', 'Kubernetes', 'AWS', 'GCP', 'Azure',
    'TensorFlow', 'PyTorch', 'Machine Learning', 'Deep Learning', 'NLP', 'Computer Vision',
    'Blockchain', 'Solidity', 'Web3', 'Git', 'Linux', 'REST APIs', 'GraphQL'
  ];

  const domainOptions = [
    'AI / Machine Learning', 'Data Science', 'Web Development', 'Mobile Development',
    'Cloud Computing', 'Cybersecurity', 'Blockchain', 'DevOps', 'IoT', 'Game Development'
  ];

  // Opportunities filter & badge helpers
  const getDeadlineDate = (item) => {
    const raw = item.deadline || item.application_deadline || item.end_date || item.event_date || item.registration_end_date || null;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };
  const hoursUntil = (item) => {
    const d = getDeadlineDate(item);
    if (!d) return null;
    return (d.getTime() - Date.now()) / 36e5;
  };
  const getCreatedDate = (item) => {
    const raw = item.created_at || item.posted_at || item.start_date || null;
    if (!raw) return null;
    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d;
  };
  const isNewItem = (item) => {
    const d = getCreatedDate(item);
    return d && (Date.now() - d.getTime()) / 86400000 < 7;
  };
  const isClosingSoon = (item) => {
    const h = hoursUntil(item);
    return h !== null && h > 0 && h <= 48;
  };
  const isTrending = (item) => {
    const count = item.applicants_count || item.applications_count || item.registration_count || item.participants_count || 0;
    return count >= 50;
  };
  const isFeatured = (item) => !!(item.is_featured || item.featured || item.is_national_competition);

  const matchSearch = (item) => {
    const q = (oppFilters.search || '').trim().toLowerCase();
    if (!q) return true;
    const hay = [
      item.title, item.company_name, item.location, item.job_type,
      item.difficulty, item.short_description, item.description,
      ...(Array.isArray(item.tags) ? item.tags : []),
      ...(Array.isArray(item.skills) ? item.skills : []),
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(q);
  };
  const sortItems = (items) => {
    const arr = [...(items || [])];
    if (oppFilters.sort === 'closing') {
      return arr.sort((a, b) => {
        const da = getDeadlineDate(a)?.getTime() || Infinity;
        const db = getDeadlineDate(b)?.getTime() || Infinity;
        return da - db;
      });
    }
    if (oppFilters.sort === 'alpha') {
      return arr.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    }
    // newest
    return arr.sort((a, b) => {
      const da = getCreatedDate(a)?.getTime() || 0;
      const db = getCreatedDate(b)?.getTime() || 0;
      return db - da;
    });
  };
  const filterItems = (items) => sortItems((items || []).filter(matchSearch));
  const showSection = (section) => oppFilters.type === 'all' || oppFilters.type === section;

  // Badge component
  const OppBadges = ({ item }) => {
    const badges = [];
    if (isFeatured(item)) badges.push({ label: 'Featured', cls: 'bg-[#0A0A0A] text-white' });
    if (isClosingSoon(item)) badges.push({ label: 'Closing Soon', cls: 'bg-[#FEF2F2] text-[#B91C1C] border border-[#B91C1C]/20' });
    if (isTrending(item)) badges.push({ label: 'Trending', cls: 'bg-[#FFFBEB] text-[#D97706] border border-[#D97706]/20' });
    if (isNewItem(item) && !isFeatured(item)) badges.push({ label: 'New', cls: 'bg-[#ECFDF5] text-[#059669] border border-[#059669]/20' });
    if (badges.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mb-2">
        {badges.map((b, i) => (
          <span
            key={i}
            className={`px-2 py-0.5 text-[9px] font-extrabold uppercase tracking-wider ${b.cls}`}
            style={{ borderRadius: '1px' }}
          >
            {b.label}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: colors.background, fontFamily: "'IBM Plex Sans', sans-serif" }}>
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center justify-between">
            <Link to="/codearena" className="flex items-center gap-2.5">
              <div className="w-8 h-8 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
                <Code size={16} className="text-white" />
              </div>
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                EvalentX Arena
              </span>
            </Link>
            <div className="flex items-center gap-3">
              {/* Notification Bell */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 text-zinc-500 hover:text-zinc-900 transition-colors"
                  data-testid="notification-bell"
                  style={{ borderRadius: '2px' }}
                >
                  {unreadCount > 0 ? (
                    <BellRing size={18} style={{ color: colors.primary }} />
                  ) : (
                    <Bell size={18} />
                  )}
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center"
                      style={{ background: colors.danger, borderRadius: '1px' }}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {/* Notification Dropdown */}
                {showNotifications && (
                  <div className="absolute right-0 top-12 w-80 bg-white border z-50 max-h-96 overflow-hidden"
                    style={{ borderColor: colors.border, borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.1)' }}>
                    <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: colors.border }}>
                      <h3 className="font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>Notifications</h3>
                      {unreadCount > 0 && (
                        <button onClick={markAllRead} className="text-xs font-medium" style={{ color: colors.primary }}>
                          Mark all read
                        </button>
                      )}
                    </div>
                    <div className="overflow-y-auto max-h-72">
                      {notifications.length > 0 ? (
                        notifications.slice(0, 10).map(notification => (
                          <div
                            key={notification.id}
                            onClick={() => !notification.is_read && markNotificationRead(notification.id)}
                            className={`p-4 border-b border-gray-50 cursor-pointer hover:bg-zinc-50 transition-colors ${
                              !notification.is_read ? 'bg-[#B91C1C10]' : ''
                            }`}
                            data-testid={`notification-${notification.id}`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-2 h-2 rounded-sm mt-2 ${
                                notification.type === 'hired' ? 'bg-[#059669]' :
                                notification.type === 'shortlisted' ? 'bg-[#D97706]' :
                                notification.type === 'interview_scheduled' ? 'bg-[#B91C1C]' :
                                notification.type === 'rejected' ? 'bg-[#b24020]' :
                                notification.type === 'success' ? 'bg-[#059669]' :
                                notification.type === 'warning' ? 'bg-[#D97706]' :
                                notification.type === 'error' ? 'bg-[#b24020]' : 'bg-[#B91C1C]'
                              }`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm ${!notification.is_read ? 'text-[#0A0A0A] font-medium' : 'text-[#71717A]'}`}>
                                  {notification.message}
                                </p>
                                <p className="text-xs text-[#71717A] mt-1">
                                  {new Date(notification.created_at).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    hour: '2-digit',
                                    minute: '2-digit'
                                  })}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="p-6 text-center text-[#71717A]">
                          <Bell className="mx-auto mb-2 text-zinc-300" size={24} />
                          <p className="text-sm">No notifications yet</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {profile.username && (
                <Link 
                  to={`/codearena/profile/${profile.username}`}
                  className="hidden md:flex items-center gap-1.5 text-sm font-medium transition-colors"
                  style={{ color: colors.primary }}
                  data-testid="view-public-profile"
                >
                  <Eye size={14} />
                  Profile
                </Link>
              )}
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 px-3 py-1.5 text-zinc-500 hover:text-red-600 transition-colors text-sm font-medium"
                style={{ borderRadius: '2px' }}
                data-testid="logout-btn"
              >
                <LogOut size={14} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid lg:grid-cols-4 gap-5">
          {/* Sidebar — hidden on mobile (mobile tab bar below provides nav) */}
          <div className="hidden lg:block lg:col-span-1 space-y-4">
            {/* Profile Summary Card */}
            <div className="bg-white border overflow-hidden" style={{ borderColor: colors.border, borderRadius: '2px' }}>
              {/* Profile Banner */}
              <div className="h-14" style={{ background: colors.primary }} />
              
              <div className="px-5 pb-5">
                {/* Avatar */}
                <div className="relative -mt-8 mb-3">
                  {avatarSrc(profile.avatar_url) ? (
                    <img
                      src={avatarSrc(profile.avatar_url)}
                      alt={profile.full_name}
                      className="w-16 h-16 object-cover border-2 border-white bg-white"
                      style={{ borderRadius: '2px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                      data-testid="sidebar-avatar-img"
                    />
                  ) : (
                    <div className="w-16 h-16 bg-white border-2 border-white flex items-center justify-center text-xl font-bold"
                      style={{ color: colors.primary, borderRadius: '2px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                      {profile.full_name?.charAt(0) || '?'}
                    </div>
                  )}
                </div>
                
                <h2 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                  {profile.full_name}
                </h2>
                {profile.username && (
                  <p className="text-xs mt-0.5" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>@{profile.username}</p>
                )}
                
                {/* Tier Badge */}
                <div 
                  className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-bold tracking-[0.06em] uppercase"
                  style={{ backgroundColor: `${tier.color}12`, color: tier.color, borderRadius: '1px' }}
                >
                  <TierIcon size={12} />
                  {tier.name}
                </div>

                {/* Quick Stats */}
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="text-center p-3 border" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                    <div className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: colors.primary }}>{stats?.total_score || 0}</div>
                    <div className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: colors.textTertiary }}>Score</div>
                  </div>
                  <div className="text-center p-3 border" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                    <div className="text-lg font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: colors.warning }}>#{stats?.rank || '-'}</div>
                    <div className="text-[10px] font-bold tracking-[0.1em] uppercase" style={{ color: colors.textTertiary }}>Rank</div>
                  </div>
                </div>

                {/* Profile Completion */}
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span style={{ color: colors.textSecondary }}>Profile Strength</span>
                    <span className="font-bold" style={{ fontFamily: "'IBM Plex Mono', monospace", color: colors.primary }}>{stats?.profile_completion || 0}%</span>
                  </div>
                  <div className="w-full h-1 overflow-hidden" style={{ background: colors.subtle, borderRadius: '0' }}>
                    <div className="h-full transition-all" style={{ width: `${stats?.profile_completion || 0}%`, background: colors.primary }} />
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <div className="bg-white border p-3" style={{ borderColor: colors.border, borderRadius: '2px' }}>
              <nav className="space-y-0.5">
                {[
                  { id: 'overview', label: 'Overview', icon: User },
                  { id: 'opportunities', label: 'Opportunities', icon: Briefcase },
                  { id: 'mock-interview', label: 'Mock Interview', icon: MessageSquare, link: '/codearena/mock-interview' },
                  { id: 'profile', label: 'Edit Profile', icon: Settings },
                  { id: 'projects', label: 'Projects', icon: FolderGit2 },
                  { id: 'achievements', label: 'Achievements', icon: Trophy },
                  { id: 'submissions', label: 'Submissions', icon: Code },
                  { id: 'courses', label: 'My Courses', icon: BookOpen },
                  { id: 'purchases', label: 'Purchases', icon: FileText },
                ].map((item) => {
                  const Icon = item.icon;
                  if (item.link) {
                    return (
                      <button key={item.id}
                        onClick={() => navigate(item.link)}
                        className="w-full flex items-center gap-2.5 px-3 py-2.5 transition-all text-sm font-medium"
                        style={{ color: colors.textSecondary, borderRadius: '2px' }}
                        data-testid={`nav-${item.id}`}
                        onMouseEnter={e => { e.currentTarget.style.background = colors.subtle; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <Icon size={16} />
                        {item.label}
                        <ChevronRight size={14} className="ml-auto" style={{ color: colors.textTertiary }} />
                      </button>
                    );
                  }
                  const isActive = activeTab === item.id;
                  return (
                    <button key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-2.5 px-3 py-2.5 transition-all text-sm font-medium`}
                      style={{
                        background: isActive ? colors.primaryLight : 'transparent',
                        color: isActive ? colors.primary : colors.textSecondary,
                        borderLeft: isActive ? `2px solid ${colors.primary}` : '2px solid transparent',
                        borderRadius: '0'
                      }}
                      data-testid={`nav-${item.id}`}
                      onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = colors.subtle; }}
                      onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                    >
                      <Icon size={16} />
                      {item.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 min-w-0">
            {/* Mobile Tab Bar */}
            <div className="lg:hidden flex gap-1 overflow-x-auto pb-3 mb-3" style={{ scrollbarWidth: 'none' }}>
              {['overview', 'opportunities', 'profile', 'projects', 'achievements', 'submissions', 'courses', 'purchases'].map((tab) => (
                <button key={tab} onClick={() => setActiveTab(tab)} data-testid={`mobile-tab-${tab}`}
                  className="shrink-0 px-3 py-2 text-[10px] font-bold tracking-wide uppercase transition-all"
                  style={{
                    background: activeTab === tab ? colors.primary : '#fff',
                    color: activeTab === tab ? '#fff' : colors.textSecondary,
                    border: `1px solid ${activeTab === tab ? colors.primary : colors.border}`,
                    borderRadius: '2px'
                  }}>
                  {tab.replace('-', ' ')}
                </button>
              ))}
            </div>

            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="space-y-5">
                {/* Welcome Strip */}
                <div className="bg-white border p-5 sm:p-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
                  style={{ borderColor: colors.border, borderRadius: '2px' }}
                  data-testid="welcome-strip">
                  <div>
                    <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: colors.textTertiary }}>
                      Welcome back
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tighter" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                      {profile.full_name?.split(' ')[0] || 'Builder'}.
                    </h1>
                    <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                      Your career command center. Apply, practice, compete, get verified.
                    </p>
                  </div>
                  <div className="flex items-center gap-5">
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>Profile Strength</p>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="w-32 h-1.5" style={{ background: colors.subtle }}>
                          <div className="h-full transition-all" style={{ width: `${stats?.profile_completion || 0}%`, background: colors.primary }} />
                        </div>
                        <span className="text-sm font-bold tabular-nums" style={{ color: colors.primary, fontFamily: "'IBM Plex Mono', monospace" }}>
                          {stats?.profile_completion || 0}%
                        </span>
                      </div>
                    </div>
                    {profile?.username && (
                      <ShareButton
                        kind="profile"
                        id={profile.username}
                        label="Share Profile"
                        variant="solid"
                        size="md"
                        testid="dashboard-share-profile-btn"
                      />
                    )}
                  </div>
                </div>

                {/* Share My Verified Profile — visible whenever the badge is active.
                    Source of truth is the `verified_talent` block from the API; we still
                    fall back to the (possibly stale) `is_verified_talent` flag so older
                    profile records keep working. */}
                {(verifiedTalent?.is_verified || profile.is_verified_talent) && (profile.username || profile.id) && (
                  <VerifiedShareCard
                    username={profile.username || profile.id}
                    fullName={profile.full_name}
                    badgeExpires={verifiedTalent?.badge_expires_at || profile.verified_badge_expires_at}
                    planCode={verifiedTalent?.plan_code || profile.verified_plan_code}
                  />
                )}

                <MyHackathonsCard token={token} />

                {/* Featured CTAs — Mock Interview & Verified Profile (high priority) */}
                <div data-testid="featured-cta-section">
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>Featured for you</p>
                      <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                        Get hired faster
                      </h2>
                    </div>
                    <span className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>
                      <span className="inline-block w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: colors.primary }} />
                      Limited spots this week
                    </span>
                  </div>

                  <div className="grid lg:grid-cols-2 gap-3" data-testid="featured-cta-grid">
                    {/* FEATURED: Start Mock Interview */}
                    <Link to="/codearena/mock-interview"
                      data-testid="feature-card-mock-interview"
                      className="group relative bg-white border-2 p-6 transition-all overflow-hidden flex flex-col"
                      style={{ borderColor: colors.text, borderRadius: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(185,28,28,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = colors.text; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {/* Diagonal accent stripe */}
                      <div className="absolute top-0 right-0 w-24 h-24 overflow-hidden pointer-events-none">
                        <div className="absolute -top-3 -right-12 w-32 h-6" style={{ background: colors.primary, transform: 'rotate(45deg)' }} />
                      </div>

                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase" style={{ background: colors.text, color: '#fff', borderRadius: '1px' }}>
                          ★ Top pick
                        </span>
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase border" style={{ borderColor: colors.success, color: colors.success, background: '#ECFDF5', borderRadius: '1px' }}>
                          Free
                        </span>
                      </div>

                      <div className="mt-7 w-12 h-12 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
                        <Mic size={22} className="text-white" />
                      </div>

                      <h3 className="text-xl font-extrabold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                        Start Mock Interview
                      </h3>
                      <p className="text-sm leading-relaxed mt-1.5" style={{ color: colors.textSecondary }}>
                        Voice-based AI interview with Athena. Scored on communication, technical depth, and confidence — instant feedback report.
                      </p>

                      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold" style={{ color: colors.textSecondary }}>
                        <span className="flex items-center gap-1"><CheckCircle size={11} style={{ color: colors.success }} /> Real recruiter Qs</span>
                        <span className="flex items-center gap-1"><CheckCircle size={11} style={{ color: colors.success }} /> Instant scoring</span>
                        <span className="flex items-center gap-1"><CheckCircle size={11} style={{ color: colors.success }} /> ~15 min</span>
                      </div>

                      <div className="mt-auto pt-5">
                        <span className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase text-white transition-colors group-hover:bg-[#7F1D1D]" style={{ background: colors.primary, borderRadius: '2px' }}>
                          Start Session Now <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>

                    {/* FEATURED: Get Verified Profile */}
                    <Link to="/verified-talent"
                      data-testid="feature-card-verified-profile"
                      className="group relative border-2 p-6 transition-all overflow-hidden flex flex-col"
                      style={{ borderColor: colors.text, background: colors.text, borderRadius: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(10,10,10,0.25)'; }}
                      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      {/* Subtle gold gleam */}
                      <div className="absolute -top-12 -right-12 w-40 h-40 pointer-events-none opacity-20" style={{ background: 'radial-gradient(circle, #FCD34D 0%, transparent 70%)' }} />

                      <div className="absolute top-3 left-3 flex items-center gap-1.5">
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase" style={{ background: '#FCD34D', color: '#7C2D12', borderRadius: '1px' }}>
                          ★ Premium
                        </span>
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase border border-white/30 text-white" style={{ borderRadius: '1px' }}>
                          Recruiter-trusted
                        </span>
                      </div>

                      <div className="mt-7 w-12 h-12 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FCD34D, #F59E0B)', borderRadius: '2px' }}>
                        <ShieldCheck size={22} style={{ color: '#7C2D12' }} />
                      </div>

                      <h3 className="text-xl font-extrabold mt-4 tracking-tight text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                        Get Verified Profile
                      </h3>
                      <p className="text-sm leading-relaxed mt-1.5 text-white/75">
                        AI-verified badge proves your skills are real. Priority visibility, recruiter-shareable public profile, certificate, and 2x callbacks on average.
                      </p>

                      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-semibold text-white/80">
                        <span className="flex items-center gap-1"><CheckCircle size={11} className="text-amber-300" /> Verified badge</span>
                        <span className="flex items-center gap-1"><CheckCircle size={11} className="text-amber-300" /> Priority listing</span>
                        <span className="flex items-center gap-1"><CheckCircle size={11} className="text-amber-300" /> Shareable URL</span>
                      </div>

                      <div className="mt-auto pt-5">
                        <span className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold tracking-[0.1em] uppercase transition-transform group-hover:translate-x-0.5"
                          style={{ background: '#FCD34D', color: '#7C2D12', borderRadius: '2px' }}>
                          Get Verified <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                        </span>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Secondary tracks */}
                <div>
                  <div className="flex items-end justify-between mb-3">
                    <div>
                      <p className="text-[10px] font-bold tracking-[0.2em] uppercase" style={{ color: colors.textTertiary }}>Explore</p>
                      <h2 className="text-base font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                        More tracks
                      </h2>
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3" data-testid="feature-hero-grid">
                    {/* Card: Jobs & Internships */}
                    <Link to="/codearena/job-board"
                      data-testid="feature-card-jobs"
                      className="group relative bg-white border p-5 transition-all overflow-hidden"
                      style={{ borderColor: colors.border, borderRadius: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(185,28,28,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase" style={{ background: colors.primary, color: '#fff', borderRadius: '1px' }}>
                          Popular
                        </span>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }}>
                        <Briefcase size={18} style={{ color: colors.primary }} />
                      </div>
                      <h3 className="text-base font-bold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                        Jobs &amp; Internships
                      </h3>
                      <p className="text-xs leading-relaxed mt-1" style={{ color: colors.textSecondary }}>
                        Apply to verified hiring partners. Track every application from submission to offer.
                      </p>
                      <div className="flex items-center gap-1.5 mt-4 text-xs font-bold tracking-wide" style={{ color: colors.primary }}>
                        Browse Roles <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                      </div>
                    </Link>

                    {/* Card: Challenges & Contests */}
                    <Link to="/codearena/contests"
                      data-testid="feature-card-challenges"
                      className="group relative bg-white border p-5 transition-all overflow-hidden"
                      style={{ borderColor: colors.border, borderRadius: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = colors.primary; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(185,28,28,0.08)'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = colors.border; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                    >
                      <div className="absolute top-3 right-3">
                        <span className="px-2 py-0.5 text-[9px] font-bold tracking-[0.15em] uppercase" style={{ background: colors.text, color: '#fff', borderRadius: '1px' }}>
                          New
                        </span>
                      </div>
                      <div className="w-10 h-10 flex items-center justify-center border" style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }}>
                        <Trophy size={18} style={{ color: colors.primary }} />
                      </div>
                      <h3 className="text-base font-bold mt-4 tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                        Challenges &amp; Contests
                      </h3>
                      <p className="text-xs leading-relaxed mt-1" style={{ color: colors.textSecondary }}>
                        Compete in national hackathons, hiring challenges, and live contests. Win, get noticed.
                      </p>
                      <div className="flex items-center gap-1.5 mt-4 text-xs font-bold tracking-wide" style={{ color: colors.primary }}>
                        See Live Contests <ArrowRight size={12} className="transition-transform group-hover:translate-x-1" />
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Stats Strip — compact */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Problems Solved', value: stats?.problems_solved || 0, icon: Code },
                    { label: 'Achievements', value: achievements.length, icon: Trophy },
                    { label: 'Projects', value: projects.length, icon: FolderGit2 },
                    { label: 'Percentile', value: `Top ${(100 - (stats?.percentile || 0)).toFixed(0)}%`, icon: TrendingUp }
                  ].map((stat, idx) => (
                    <div key={idx} className="bg-white border p-4" style={{ borderColor: colors.border, borderRadius: '2px' }}
                      data-testid={`stat-card-${idx}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-[10px] font-bold tracking-[0.15em] uppercase" style={{ color: colors.textTertiary }}>
                            {stat.label}
                          </p>
                          <p className="text-2xl font-extrabold mt-1 tabular-nums tracking-tighter" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                            {stat.value}
                          </p>
                        </div>
                        <stat.icon size={14} style={{ color: colors.textTertiary }} />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Soft Upgrade Prompt — appears only after feature exposure */}
                {!(profile?.resume_premium || profile?.has_premium) && (
                  <div className="border p-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    style={{ borderColor: colors.border, background: colors.primaryLight, borderRadius: '2px' }}
                    data-testid="soft-upgrade-prompt">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 flex items-center justify-center" style={{ background: colors.primary, borderRadius: '2px' }}>
                        <Sparkles size={16} className="text-white" />
                      </div>
                      <div>
                        <h4 className="text-sm font-bold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: colors.text }}>
                          Ready to go further?
                        </h4>
                        <p className="text-xs mt-0.5" style={{ color: colors.textSecondary }}>
                          Unlock the Verified Talent Profile and AI-powered tools when you're ready.
                        </p>
                      </div>
                    </div>
                    <Link to="/verified-talent"
                      data-testid="soft-upgrade-cta"
                      className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold tracking-wide transition-colors"
                      style={{ background: colors.text, color: '#fff', borderRadius: '2px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = colors.primary; }}
                      onMouseLeave={e => { e.currentTarget.style.background = colors.text; }}
                    >
                      Unlock Full Access <ArrowRight size={12} />
                    </Link>
                  </div>
                )}

                {/* Recent Submissions */}
                <div className="bg-white border p-5" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                  <h3 className="text-[10px] font-bold tracking-[0.15em] uppercase mb-4" style={{ color: colors.textTertiary }}>Recent Submissions</h3>
                  {submissions.length > 0 ? (
                    <div className="space-y-1.5">
                      {submissions.slice(0, 5).map((sub, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 border" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                          <div>
                            <p className="font-medium text-sm" style={{ color: colors.text }}>{sub.problem_title || sub.problem_id}</p>
                            <p className="text-[11px]" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                              {new Date(sub.submitted_at).toLocaleString()}
                            </p>
                          </div>
                          <span className="px-2.5 py-0.5 text-[10px] font-bold tracking-wide uppercase"
                            style={{
                              background: sub.verdict === 'Accepted' ? `${colors.success}10` : `${colors.danger}10`,
                              color: sub.verdict === 'Accepted' ? colors.success : colors.danger,
                              borderRadius: '1px'
                            }}>
                            {sub.verdict}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center py-6 text-sm" style={{ color: colors.textTertiary }}>No submissions yet. Start solving problems!</p>
                  )}
                </div>
              </div>
            )}

            {/* Opportunities Tab */}
            {activeTab === 'opportunities' && (
              <div className="space-y-4">
                {/* Filter Toolbar */}
                <div className="bg-white border p-4 md:p-5 sticky top-0 z-10" style={{ borderColor: colors.border, borderRadius: '2px' }} data-testid="opportunities-filter-bar">
                  <div className="flex flex-col gap-3">
                    {/* Type Tabs + Count */}
                    <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide -mx-1 px-1">
                      {[
                        { id: 'all', label: 'All', count: (opportunities.jobs?.length || 0) + (opportunities.hiring_challenges?.length || 0) + (opportunities.enterprise_challenges?.length || 0) + (publicEvents?.length || 0) },
                        { id: 'jobs', label: 'Jobs', count: opportunities.jobs?.length || 0 },
                        { id: 'hiring', label: 'Hiring Challenges', count: opportunities.hiring_challenges?.length || 0 },
                        { id: 'enterprise', label: 'Enterprise', count: opportunities.enterprise_challenges?.length || 0 },
                        { id: 'events', label: 'Events', count: publicEvents?.length || 0 },
                      ].map(tab => (
                        <button
                          key={tab.id}
                          onClick={() => setOppFilters(f => ({ ...f, type: tab.id }))}
                          className={`flex-shrink-0 px-3.5 py-1.5 text-xs font-bold tracking-wide uppercase transition-all border ${oppFilters.type === tab.id ? 'bg-[#0A0A0A] text-white border-[#0A0A0A]' : 'bg-white text-[#525252] border-[#E5E5E5] hover:border-[#B91C1C] hover:text-[#B91C1C]'}`}
                          style={{ borderRadius: '2px' }}
                          data-testid={`opp-filter-${tab.id}`}
                        >
                          {tab.label}
                          <span className={`ml-1.5 ${oppFilters.type === tab.id ? 'text-white/70' : 'text-[#A3A3A3]'}`}>{tab.count}</span>
                        </button>
                      ))}
                    </div>

                    {/* Search + Sort */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <div className="relative flex-1">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
                        <input
                          type="text"
                          value={oppFilters.search}
                          onChange={e => setOppFilters(f => ({ ...f, search: e.target.value }))}
                          placeholder="Search by title, company, or tag…"
                          className="w-full pl-9 pr-3 py-2 text-sm border focus:border-[#B91C1C] outline-none"
                          style={{ borderColor: colors.border, borderRadius: '2px', color: colors.text }}
                          data-testid="opp-search"
                        />
                      </div>
                      <select
                        value={oppFilters.sort}
                        onChange={e => setOppFilters(f => ({ ...f, sort: e.target.value }))}
                        className="px-3 py-2 text-sm border focus:border-[#B91C1C] outline-none bg-white cursor-pointer"
                        style={{ borderColor: colors.border, borderRadius: '2px', color: colors.text }}
                        data-testid="opp-sort"
                      >
                        <option value="newest">Newest first</option>
                        <option value="closing">Closing soon</option>
                        <option value="alpha">A–Z</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* My Applications Section */}
                <div className="bg-white border p-5" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[10px] font-bold tracking-[0.12em] uppercase" style={{ color: colors.textTertiary }}>
                      My Applications
                    </h3>
                    <span className="text-xs font-medium" style={{ color: colors.textTertiary, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {myApplications.length} total
                    </span>
                  </div>
                  
                  {myApplications.length > 0 ? (
                    <div className="space-y-3">
                      {myApplications.map((app) => {
                        const statusConfig = {
                          pending: { color: colors.primary, icon: Clock, label: 'Pending', step: 1 },
                          submitted: { color: colors.primary, icon: CheckCircle, label: 'Submitted', step: 1 },
                          reviewed: { color: colors.primary, icon: Eye, label: 'Under Review', step: 2 },
                          assessment: { color: colors.warning, icon: Play, label: 'Assessment', step: 2 },
                          shortlisted: { color: '#7c3aed', icon: Star, label: 'Shortlisted', step: 3 },
                          interview: { color: '#7c3aed', icon: Calendar, label: 'Interview', step: 3 },
                          selected: { color: colors.success, icon: Trophy, label: 'Selected', step: 4 },
                          hired: { color: colors.success, icon: Trophy, label: 'Hired!', step: 4 },
                          rejected: { color: colors.danger, icon: XCircle, label: 'Not Selected', step: 0 }
                        };
                        const status = statusConfig[app.status] || statusConfig.pending;
                        const StatusIcon = status.icon;
                        
                        return (
                          <div 
                            key={app.id}
                            className="p-4 border transition-colors"
                            style={{ borderColor: colors.border, borderRadius: '2px' }}
                            data-testid={`application-${app.id}`}
                          >
                            <div className="flex items-start justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h4 className="font-medium text-sm" style={{ color: colors.text }}>{app.job?.title || app.job_title || 'Job Position'}</h4>
                                  <span 
                                    className="px-2 py-0.5 text-[10px] font-bold"
                                    style={{ backgroundColor: `${status.color}12`, color: status.color, borderRadius: '1px' }}
                                  >
                                    <StatusIcon size={10} className="inline mr-1" />
                                    {status.label}
                                  </span>
                                </div>
                                <p className="text-xs flex items-center gap-1.5" style={{ color: colors.textSecondary }}>
                                  <Building size={12} />
                                  {app.job?.company_name || app.company_name || 'Company'}
                                </p>
                                <p className="text-xs mt-1" style={{ color: colors.textTertiary }}>
                                  Applied {new Date(app.applied_at).toLocaleDateString('en-IN')}
                                </p>
                              </div>
                              
                              {/* Status Progress Bar */}
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-0.5">
                                  {[1, 2, 3, 4].map((step) => (
                                    <div
                                      key={step}
                                      className="w-6 h-1"
                                      style={{
                                        background: app.status === 'rejected' ? `${colors.danger}30` : step <= status.step ? colors.primary : colors.border,
                                        borderRadius: '0'
                                      }}
                                    />
                                  ))}
                                </div>
                                <span className="text-[10px]" style={{ color: colors.textTertiary }}>
                                  {app.status === 'rejected' ? 'Closed' : `Step ${status.step}/4`}
                                </span>
                              </div>
                            </div>
                            
                            {/* Status-specific messages */}
                            {app.status === 'shortlisted' && (
                              <div className="mt-3 p-2.5 bg-[#7c3aed]/10 border border-[#7c3aed]/20 rounded-sm">
                                <p className="text-[#7c3aed] text-sm flex items-center gap-1.5">
                                  <Star size={12} />
                                  <span className="font-medium">Congratulations!</span> You've been shortlisted
                                </p>
                              </div>
                            )}
                            {app.status === 'hired' && (
                              <div className="mt-3 p-2.5 bg-[#059669]/10 border border-[#057642]/20 rounded-sm">
                                <p className="text-[#059669] text-sm flex items-center gap-1.5">
                                  <Trophy size={12} />
                                  <span className="font-medium">Congratulations!</span> You've been hired!
                                </p>
                              </div>
                            )}
                            {app.status === 'assessment' && (
                              <div className="mt-3 p-2.5 bg-[#D97706]/10 border border-[#915907]/20 rounded-sm">
                                <p className="text-[#D97706] text-sm flex items-center gap-1.5">
                                  <AlertCircle size={12} />
                                  <span className="font-medium">Action Required:</span> Complete assessment
                                </p>
                                <Link 
                                  to={`/codearena/assessment/${app.assessment_id}`}
                                  className="inline-flex items-center gap-1 text-[#D97706] text-sm mt-1 hover:underline font-medium"
                                >
                                  Start Assessment <ArrowRight size={12} />
                                </Link>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="mx-auto text-zinc-300 mb-3" size={40} />
                      <p className="text-[#71717A] text-sm">No applications yet</p>
                      <p className="text-[#71717A] text-xs mt-1">Apply to jobs below to get started!</p>
                    </div>
                  )}
                </div>

                {/* Jobs Section */}
                {showSection('jobs') && (
                <div className="bg-white border p-5" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[10px] font-bold tracking-[0.12em] uppercase flex items-center gap-2" style={{ color: colors.textTertiary }}>
                      <Briefcase size={14} style={{ color: colors.success }} />
                      Jobs & Internships
                    </h3>
                    <Link to="/codearena/job-board" className="text-xs font-bold flex items-center gap-1 transition-colors" style={{ color: colors.primary }}>
                      View All <ChevronRight size={12} />
                    </Link>
                  </div>
                  
                  {opportunitiesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader className="animate-spin" style={{ color: colors.primary }} size={24} />
                    </div>
                  ) : (() => {
                    const list = filterItems(opportunities.jobs).slice(0, 12);
                    if (list.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Briefcase className="mx-auto mb-3" size={40} style={{ color: colors.border }} />
                          <p className="text-sm" style={{ color: colors.textTertiary }}>{oppFilters.search ? 'No jobs match your search' : 'No jobs available at the moment'}</p>
                        </div>
                      );
                    }
                    return (
                      <div className="grid md:grid-cols-2 gap-3">
                        {list.map((job) => {
                          const hasApplied = myApplications.some(app => app.job_id === job.id);
                          const deadline = getDeadlineDate(job);
                          const closing = isClosingSoon(job);
                          return (
                            <div
                              key={job.id}
                              className={`p-4 border transition-all hover:border-[#B91C1C] hover:shadow-[0_8px_24px_-12px_rgba(185,28,28,0.18)] flex flex-col ${closing ? 'border-l-2 border-l-[#B91C1C]' : ''}`}
                              style={{ borderColor: colors.border, borderRadius: '2px' }}
                              data-testid={`job-card-${job.id}`}
                            >
                              <OppBadges item={job} />
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                  <h4 className="font-bold text-sm leading-snug truncate" style={{ color: colors.text }}>{job.title}</h4>
                                  <p className="text-xs flex items-center gap-1 truncate" style={{ color: colors.textSecondary }}>
                                    <Building size={10} />
                                    {job.company_name || 'Company'}
                                  </p>
                                </div>
                                {job.is_national_competition && (
                                  <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase flex items-center gap-1 flex-shrink-0"
                                    style={{ background: `${colors.warning}12`, color: colors.warning, borderRadius: '1px' }}>
                                    <Crown size={10} /> National
                                  </span>
                                )}
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5 text-xs mb-3" style={{ color: colors.textTertiary }}>
                                {job.location && (
                                  <span className="flex items-center gap-0.5"><MapPin size={10} /> {job.location}</span>
                                )}
                                {job.job_type && (
                                  <span className="px-1.5 py-0.5 capitalize" style={{ background: colors.subtle, borderRadius: '1px' }}>{job.job_type}</span>
                                )}
                                {(job.salary_min || job.salary_max || job.salary) && (
                                  <span className="flex items-center gap-0.5 font-bold" style={{ color: colors.text }}>
                                    <DollarSign size={10} />
                                    {job.salary || `${job.salary_min || ''}${job.salary_max ? '–' + job.salary_max : ''}`}
                                  </span>
                                )}
                                {deadline && (
                                  <span className={`flex items-center gap-0.5 ${closing ? 'font-bold text-[#B91C1C]' : ''}`}>
                                    <Clock size={10} /> {closing ? `Closes in ${Math.max(1, Math.round(hoursUntil(job)))}h` : `Apply by ${deadline.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}`}
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center justify-between mt-auto pt-2 border-t gap-2" style={{ borderColor: colors.border }}>
                                {hasApplied ? (
                                  <span className="text-xs flex items-center gap-1 font-bold" style={{ color: colors.success }}>
                                    <CheckCircle size={12} /> Applied
                                  </span>
                                ) : (
                                  <span className="text-xs font-bold" style={{ color: colors.primary }}>Open</span>
                                )}
                                <Link
                                  to={`/codearena/job-board?job=${job.id}`}
                                  className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-[#0A0A0A] text-white hover:bg-[#B91C1C] transition-colors"
                                  style={{ borderRadius: '2px' }}
                                  data-testid={`job-cta-${job.id}`}
                                >
                                  {hasApplied ? 'View' : 'Apply'} <ArrowRight size={11} />
                                </Link>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
                )}

                {/* Hiring Challenges Section */}
                {showSection('hiring') && (
                <div className="bg-white border p-5" style={{ borderColor: colors.border, borderRadius: '2px' }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-[10px] font-bold tracking-[0.12em] uppercase flex items-center gap-2" style={{ color: colors.textTertiary }}>
                      <Code size={14} style={{ color: '#7c3aed' }} />
                      Hiring Challenges
                    </h3>
                    <Link to="/codearena/contests" className="text-xs font-bold flex items-center gap-1 transition-colors" style={{ color: colors.primary }}>
                      View All <ChevronRight size={12} />
                    </Link>
                  </div>
                  
                  {(() => {
                    const list = filterItems(opportunities.hiring_challenges).slice(0, 12);
                    if (list.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Code className="mx-auto mb-3" size={40} style={{ color: colors.border }} />
                          <p className="text-sm" style={{ color: colors.textTertiary }}>{oppFilters.search ? 'No challenges match your search' : 'No hiring challenges available'}</p>
                          {!oppFilters.search && <p className="text-xs mt-1" style={{ color: colors.textTertiary }}>Check back later for new opportunities</p>}
                        </div>
                      );
                    }
                    return (
                    <div className="grid md:grid-cols-2 gap-3">
                      {list.map((challenge) => {
                        const isRegistered = myChallenges.hiring_challenges.some(c => c.challenge_id === challenge.id);
                        const isLive = challenge.status === 'live';
                        const closing = isClosingSoon(challenge);
                        const deadline = getDeadlineDate(challenge);
                        return (
                          <div 
                            key={challenge.id}
                            className={`p-4 border transition-all hover:border-[#B91C1C] hover:shadow-[0_8px_24px_-12px_rgba(185,28,28,0.18)] flex flex-col ${closing ? 'border-l-2 border-l-[#B91C1C]' : ''}`}
                            style={{ borderColor: colors.border, borderRadius: '2px' }}
                            data-testid={`hiring-challenge-${challenge.id}`}
                          >
                            <OppBadges item={challenge} />
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div className="min-w-0">
                                <h4 className="font-bold text-sm leading-snug truncate" style={{ color: colors.text }}>{challenge.title}</h4>
                                <p className="text-xs flex items-center gap-1 truncate" style={{ color: colors.textSecondary }}>
                                  <Building size={10} /> {challenge.company_name}
                                </p>
                              </div>
                              <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide uppercase flex-shrink-0"
                                style={{ background: isLive ? `${colors.success}12` : `${colors.primary}12`, color: isLive ? colors.success : colors.primary, borderRadius: '1px' }}>
                                {isLive ? 'Live' : 'Upcoming'}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap items-center gap-1.5 text-xs mb-3" style={{ color: colors.textTertiary }}>
                              {challenge.difficulty && (
                                <span className="px-1.5 py-0.5 capitalize" style={{
                                  background: challenge.difficulty === 'easy' ? `${colors.success}12` : challenge.difficulty === 'medium' ? `${colors.warning}12` : `${colors.danger}12`,
                                  color: challenge.difficulty === 'easy' ? colors.success : challenge.difficulty === 'medium' ? colors.warning : colors.danger,
                                  borderRadius: '1px'
                                }}>{challenge.difficulty}</span>
                              )}
                              {challenge.participants_count > 0 && (
                                <span className="flex items-center gap-0.5">
                                  <Users size={10} /> {challenge.participants_count}
                                </span>
                              )}
                              {deadline && (
                                <span className={`flex items-center gap-0.5 ${closing ? 'font-bold text-[#B91C1C]' : ''}`}>
                                  <Clock size={10} /> {closing ? `Ends in ${Math.max(1, Math.round(hoursUntil(challenge)))}h` : deadline.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center justify-between mt-auto pt-2 border-t gap-2" style={{ borderColor: colors.border }}>
                              {isRegistered ? (
                                <span className="text-xs flex items-center gap-1 font-bold" style={{ color: colors.success }}>
                                  <CheckCircle size={12} /> Registered
                                </span>
                              ) : (
                                <span className="text-xs font-bold" style={{ color: colors.primary }}>Open</span>
                              )}
                              <Link 
                                to={`/codearena/hiring-challenges/${challenge.id}`}
                                className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-[#0A0A0A] text-white hover:bg-[#B91C1C] transition-colors"
                                style={{ borderRadius: '2px' }}
                                data-testid={`hiring-cta-${challenge.id}`}
                              >
                                {isRegistered ? 'View' : 'Participate'} <ArrowRight size={11} />
                              </Link>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    );
                  })()}
                </div>
                )}

                {/* Enterprise Challenges Section */}
                {showSection('enterprise') && (
                <div className="bg-white border p-5" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                      <Trophy className="text-[#D97706]" size={18} />
                      Enterprise Challenges
                    </h3>
                  </div>
                  
                  {(() => {
                    const list = filterItems(opportunities.enterprise_challenges).slice(0, 12);
                    if (list.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Trophy className="mx-auto text-zinc-300 mb-3" size={40} />
                          <p className="text-[#71717A] text-sm">{oppFilters.search ? 'No challenges match your search' : 'No enterprise challenges available'}</p>
                          {!oppFilters.search && <p className="text-[#71717A] text-xs mt-1">Check back later for competitions</p>}
                        </div>
                      );
                    }
                    return (
                    <div className="grid md:grid-cols-2 gap-4">
                      {list.map((challenge) => {
                        const isRegistered = myChallenges.enterprise_challenges.some(c => c.challenge_id === challenge.id);
                        const isLive = challenge.status === 'live';
                        return (
                          <Link 
                            key={challenge.id}
                            to={`/enterprise/${challenge.org_slug}/${challenge.id}`}
                            className="block bg-white rounded-sm overflow-hidden border border-[#E4E4E7] hover:shadow-md hover:border-[#B91C1C]/30 transition-all"
                            data-testid={`enterprise-challenge-${challenge.id}`}
                          >
                            {/* Banner */}
                            <div className="h-24 bg-gradient-to-br from-[#B91C1C] to-[#7F1D1D] relative">
                              {(challenge.banner_image || challenge.landing_page?.hero_image) ? (
                                <img 
                                  src={challenge.banner_image || challenge.landing_page?.hero_image} 
                                  alt={challenge.title}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="absolute inset-0 flex items-center justify-center">
                                  <Trophy className="text-white/20" size={32} />
                                </div>
                              )}
                              <div className="absolute top-2 right-2 flex gap-1">
                                <span className={`px-2 py-0.5 text-xs rounded-sm font-medium ${
                                  isLive 
                                    ? 'bg-[#059669] text-white' 
                                    : 'bg-[#B91C1C] text-white'
                                }`}>
                                  {isLive ? 'Live' : 'Upcoming'}
                                </span>
                              </div>
                              {isRegistered && (
                                <div className="absolute top-2 left-2">
                                  <span className="px-2 py-0.5 bg-[#059669] text-white text-xs rounded-sm flex items-center gap-1">
                                    <CheckCircle size={10} /> Registered
                                  </span>
                                </div>
                              )}
                            </div>
                            
                            <div className="p-4">
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                  <h4 className="font-semibold text-[#0A0A0A] text-sm line-clamp-1">{challenge.title}</h4>
                                  <p className="text-[#71717A] text-xs flex items-center gap-1 mt-0.5">
                                    <Building size={10} />
                                    {challenge.company_name || 'Company'}
                                  </p>
                                </div>
                                {challenge.challenge_type && (
                                  <span className="px-2 py-0.5 bg-[#7c3aed]/10 text-[#7c3aed] text-xs rounded-sm capitalize shrink-0">
                                    {challenge.challenge_type}
                                  </span>
                                )}
                              </div>
                              
                              {challenge.short_description && (
                                <p className="text-xs text-[#71717A] line-clamp-2 mb-2">{challenge.short_description}</p>
                              )}
                              
                              {/* Perks/Rewards */}
                              {challenge.perks && challenge.perks.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                  {challenge.perks.slice(0, 2).map((perk, idx) => (
                                    <span key={idx} className="px-1.5 py-0.5 bg-[#D97706]/10 text-[#D97706] text-xs rounded border border-[#D97706]/20">
                                      {typeof perk === 'string' ? perk : perk.title}
                                    </span>
                                  ))}
                                  {challenge.perks.length > 2 && (
                                    <span className="px-1.5 py-0.5 bg-gray-100 text-[#71717A] text-xs rounded">
                                      +{challenge.perks.length - 2}
                                    </span>
                                  )}
                                </div>
                              )}
                              
                              <div className="flex items-center justify-between pt-2 border-t border-[#F4F4F5] text-xs">
                                <span className="text-[#71717A] flex items-center gap-1">
                                  <Users size={10} /> {challenge.registration_count || 0} registered
                                </span>
                                <span className="text-[#B91C1C] font-medium">
                                  {isRegistered ? 'View Details' : 'Register Now'} →
                                </span>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                    );
                  })()}
                </div>
                )}

                {/* Events Section */}
                {showSection('events') && (
                <div className="bg-white border p-5" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                      <Calendar className="text-[#B91C1C]" size={18} />
                      Events
                    </h3>
                    <Link to="/codearena/events" className="text-[#B91C1C] text-sm hover:text-[#7F1D1D] font-medium flex items-center gap-1">
                      View All <ChevronRight size={14} />
                    </Link>
                  </div>

                  {(() => {
                    const list = filterItems(publicEvents).slice(0, 12);
                    if (list.length === 0) {
                      return (
                        <div className="text-center py-8">
                          <Calendar className="mx-auto text-zinc-300 mb-3" size={40} />
                          <p className="text-[#71717A] text-sm">{oppFilters.search ? 'No events match your search' : 'No events available right now'}</p>
                          {!oppFilters.search && <p className="text-[#71717A] text-xs mt-1">Check back later for upcoming events</p>}
                        </div>
                      );
                    }
                    return (
                    <div className="grid md:grid-cols-2 gap-3">
                      {list.map((event) => (
                        <Link
                          key={event.id}
                          to={`/events/${event.id}`}
                          className="p-4 bg-white rounded-sm hover:border-[#B91C1C] hover:shadow-[0_8px_24px_-12px_rgba(185,28,28,0.18)] transition-all border border-[#E5E5E5] flex flex-col"
                          data-testid={`event-opp-${event.id}`}
                        >
                          <OppBadges item={event} />
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="min-w-0">
                              <h4 className="font-bold text-[#0A0A0A] text-sm truncate">{event.title}</h4>
                              <p className="text-[#71717A] text-xs flex items-center gap-1 truncate">
                                <Building size={10} />
                                {event.company_name}
                              </p>
                            </div>
                            <span className={`px-2 py-0.5 text-xs rounded-sm font-medium flex-shrink-0 ${event.is_paid ? 'bg-[#7c3aed]/10 text-[#7c3aed]' : 'bg-[#059669]/10 text-[#059669]'}`}>
                              {event.is_paid ? `₹${event.price}` : 'Free'}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1.5 text-xs text-[#71717A] mb-3">
                            <span className="flex items-center gap-0.5">
                              <Calendar size={10} /> {event.event_date ? new Date(event.event_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : 'TBD'}
                            </span>
                            <span className="flex items-center gap-0.5">
                              {event.event_type === 'offline' ? <MapPin size={10} /> : <Globe size={10} />}
                              {event.event_type === 'offline' ? (event.venue || 'Venue TBD') : 'Online'}
                            </span>
                          </div>
                          <div className="flex items-center justify-between mt-auto pt-2 border-t border-[#E4E4E7] gap-2">
                            <span className="text-xs text-[#71717A] flex items-center gap-1">
                              <Users size={10} /> {event.registration_count || 0}
                            </span>
                            <span className="inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-[#0A0A0A] text-white" style={{ borderRadius: '2px' }}>
                              View <ArrowRight size={11} />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                    );
                  })()}
                </div>
                )}

                {/* My Registered Events */}
                {myRegisteredEvents.length > 0 && (
                  <div className="bg-white border p-5" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }} data-testid="my-registered-events">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                        <Ticket className="text-[#B91C1C]" size={18} />
                        My Registered Events
                      </h3>
                      <span className="text-sm text-[#71717A]">{myRegisteredEvents.length} events</span>
                    </div>
                    <div className="space-y-3">
                      {myRegisteredEvents.map((reg) => (
                        <div
                          key={reg.registration_id}
                          className="flex items-center justify-between p-4 bg-[#F4F4F5] rounded-sm border border-transparent hover:border-[#E4E4E7] transition-colors"
                          data-testid={`my-event-${reg.event?.id}`}
                        >
                          <Link to={`/events/${reg.event?.id}`} className="flex items-center gap-3 min-w-0 flex-1">
                            {reg.event?.cover_image ? (
                              <img src={reg.event.cover_image} alt="" className="w-12 h-12 rounded object-cover" />
                            ) : (
                              <div className="w-12 h-12 rounded bg-[#B91C1C]/10 flex items-center justify-center">
                                <Calendar size={18} className="text-[#B91C1C]" />
                              </div>
                            )}
                            <div className="min-w-0">
                              <h4 className="font-medium text-[#0A0A0A] text-sm truncate">{reg.event?.title}</h4>
                              <p className="text-[#71717A] text-xs flex items-center gap-2 mt-0.5">
                                <span>{reg.event?.company_name}</span>
                                <span>·</span>
                                <span>{reg.event?.event_date ? new Date(reg.event.event_date).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' }) : 'TBD'}</span>
                              </p>
                            </div>
                          </Link>
                          <div className="text-right shrink-0 ml-3">
                            <span className={`px-2 py-0.5 text-[10px] rounded font-medium ${
                              reg.payment_status === 'paid' || reg.payment_status === 'free' ? 'bg-[#059669]/10 text-[#059669]' : 'bg-amber-100 text-amber-700'
                            }`}>
                              {reg.payment_status === 'paid' ? 'Confirmed' : reg.payment_status === 'free' ? 'Registered' : 'Pending Payment'}
                            </span>
                            <Link to={`/events/${reg.event?.id}/ticket/${reg.registration_id}`}
                              className="mt-1.5 flex items-center justify-end gap-1 text-[11px] font-semibold text-[#002FA7] hover:underline"
                              data-testid={`view-ticket-${reg.event?.id}`}>
                              <Ticket size={12} /> View Ticket
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* My Challenge Registrations Section */}
                {(myChallenges.hiring_challenges.length > 0 || myChallenges.enterprise_challenges.length > 0) && (
                  <div className="bg-white border p-5" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#0A0A0A] flex items-center gap-2">
                        <Target className="text-[#B91C1C]" size={18} />
                        My Challenge Registrations
                      </h3>
                      <span className="text-sm text-[#71717A]">
                        {myChallenges.hiring_challenges.length + myChallenges.enterprise_challenges.length} registrations
                      </span>
                    </div>
                    
                    <div className="space-y-3">
                      {[...myChallenges.hiring_challenges, ...myChallenges.enterprise_challenges].slice(0, 5).map((registration) => (
                        <div 
                          key={registration.challenge_id || registration.id}
                          className="p-3 bg-[#F4F4F5] rounded-sm flex items-center justify-between"
                        >
                          <div>
                            <h4 className="font-medium text-[#0A0A0A] text-sm">
                              {registration.challenge?.title || 'Challenge'}
                            </h4>
                            <p className="text-[#71717A] text-xs flex items-center gap-2">
                              <span>{registration.challenge?.company_name}</span>
                              <span className={`px-1.5 py-0.5 rounded text-xs ${
                                registration.type === 'hiring_challenge' 
                                  ? 'bg-[#7c3aed]/10 text-[#7c3aed]' 
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {registration.type === 'hiring_challenge' ? 'Hiring' : 'Enterprise'}
                              </span>
                            </p>
                          </div>
                          <div className="text-right">
                            <span className={`text-xs px-2 py-1 rounded-sm ${
                              registration.challenge?.status === 'live' 
                                ? 'bg-[#059669]/10 text-[#059669]' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {registration.challenge?.status || 'Registered'}
                            </span>
                            {registration.score !== undefined && registration.score !== null && (
                              <p className="text-xs text-[#71717A] mt-1">Score: {registration.score}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Edit Profile Tab */}
            {activeTab === 'profile' && (
              <div className="bg-white border p-5" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-semibold text-[#0A0A0A]">Edit Profile</h3>
                  <button
                    onClick={saveProfile}
                    className="flex items-center gap-2 px-4 py-2 bg-[#B91C1C] text-white rounded-sm font-medium hover:bg-[#7F1D1D] transition-colors text-sm"
                    data-testid="save-profile-btn"
                  >
                    <Save size={16} />
                    Save Changes
                  </button>
                </div>

                <div className="space-y-5">
                  {/* Profile Photo */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Profile Photo</label>
                    <div className="flex items-center gap-4">
                      {avatarSrc(editForm.avatar_url) ? (
                        <img
                          src={avatarSrc(editForm.avatar_url)}
                          alt={editForm.full_name}
                          className="w-20 h-20 object-cover border border-[#E4E4E7] rounded-sm bg-white"
                          data-testid="edit-avatar-img"
                        />
                      ) : (
                        <div className="w-20 h-20 flex items-center justify-center text-2xl font-bold bg-[#FEF2F2] text-[#B91C1C] border border-[#E4E4E7] rounded-sm">
                          {editForm.full_name?.charAt(0) || '?'}
                        </div>
                      )}
                      <div className="flex flex-col gap-2">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          ref={avatarInputRef}
                          onChange={handleAvatarSelect}
                          className="hidden"
                          data-testid="avatar-file-input"
                        />
                        <button
                          type="button"
                          onClick={() => avatarInputRef.current?.click()}
                          disabled={avatarUploading}
                          className="inline-flex items-center gap-2 px-3.5 py-2 bg-[#171717] text-white rounded-sm font-medium hover:bg-[#0A0A0A] transition-colors text-sm disabled:opacity-60"
                          data-testid="upload-avatar-btn"
                        >
                          {avatarUploading ? <Loader size={15} className="animate-spin" /> : <Camera size={15} />}
                          {editForm.avatar_url ? 'Change photo' : 'Upload photo'}
                        </button>
                        {editForm.avatar_url && (
                          <button
                            type="button"
                            onClick={handleAvatarRemove}
                            disabled={avatarUploading}
                            className="inline-flex items-center gap-2 text-[13px] font-medium text-[#B91C1C] hover:text-[#7F1D1D] transition-colors disabled:opacity-60"
                            data-testid="remove-avatar-btn"
                          >
                            <Trash2 size={14} /> Remove
                          </button>
                        )}
                        <p className="text-xs text-[#71717A]">JPEG, PNG or WebP. Max 5MB.</p>
                      </div>
                    </div>
                  </div>

                  {/* Username */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Username</label>
                    <input
                      type="text"
                      value={editForm.username || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Choose a unique username"
                      className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors"
                      data-testid="username-input"
                    />
                    <p className="text-xs text-[#71717A] mt-1">Public URL: evalentx.com/codearena/profile/{editForm.username || 'username'}</p>
                  </div>

                  {/* Bio */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Bio</label>
                    <textarea
                      value={editForm.bio || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Tell us about yourself..."
                      rows={3}
                      className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors resize-none"
                      data-testid="bio-input"
                    />
                  </div>

                  {/* Location */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Location</label>
                    <input
                      type="text"
                      value={editForm.location || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="City, Country"
                      className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors"
                      data-testid="location-input"
                    />
                  </div>

                  {/* Looking For */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Looking For</label>
                    <select
                      value={editForm.looking_for || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, looking_for: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors"
                      data-testid="looking-for-select"
                    >
                      <option value="">Not specified</option>
                      <option value="internship">Internship</option>
                      <option value="full-time">Full-time Job</option>
                      <option value="freelance">Freelance Work</option>
                      <option value="not-looking">Not Looking</option>
                    </select>
                  </div>

                  {/* Skills */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Skills</label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {editForm.skills?.map((skill, idx) => (
                        <span 
                          key={idx}
                          className="flex items-center gap-1 px-2.5 py-1 bg-[#B91C1C10] text-[#B91C1C] rounded-sm text-sm border border-[#B91C1C]/20"
                        >
                          {skill}
                          <button onClick={() => removeSkill(skill)} className="hover:text-[#DC2626]">
                            <X size={12} />
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={newSkill}
                        onChange={(e) => setNewSkill(e.target.value)}
                        className="flex-1 px-4 py-2 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors text-sm"
                      >
                        <option value="">Select a skill...</option>
                        {skillOptions.filter(s => !editForm.skills?.includes(s)).map(skill => (
                          <option key={skill} value={skill}>{skill}</option>
                        ))}
                      </select>
                      <button
                        onClick={addSkill}
                        className="px-3 py-2 bg-[#B91C1C10] text-[#B91C1C] rounded-sm hover:bg-[#B91C1C]/20 transition-colors"
                      >
                        <Plus size={18} />
                      </button>
                    </div>
                  </div>

                  {/* Domains */}
                  <div>
                    <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">Interested Domains</label>
                    <div className="flex flex-wrap gap-2">
                      {domainOptions.map((domain) => (
                        <button
                          key={domain}
                          onClick={() => {
                            const domains = editForm.domains || [];
                            if (domains.includes(domain)) {
                              setEditForm(prev => ({ ...prev, domains: domains.filter(d => d !== domain) }));
                            } else {
                              setEditForm(prev => ({ ...prev, domains: [...domains, domain] }));
                            }
                          }}
                          className={`px-3 py-1.5 rounded-sm text-sm transition-all ${
                            editForm.domains?.includes(domain)
                              ? 'bg-[#B91C1C] text-white'
                              : 'bg-[#F4F4F5] text-[#71717A] hover:bg-zinc-100'
                          }`}
                        >
                          {domain}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Social Links */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">
                        <Github size={12} className="inline mr-1" />
                        GitHub URL
                      </label>
                      <input
                        type="url"
                        value={editForm.github_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, github_url: e.target.value }))}
                        placeholder="https://github.com/username"
                        className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors text-sm"
                        data-testid="github-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">
                        <Linkedin size={12} className="inline mr-1" />
                        LinkedIn URL
                      </label>
                      <input
                        type="url"
                        value={editForm.linkedin_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, linkedin_url: e.target.value }))}
                        placeholder="https://linkedin.com/in/username"
                        className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors text-sm"
                        data-testid="linkedin-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">
                        <Globe size={12} className="inline mr-1" />
                        Portfolio URL
                      </label>
                      <input
                        type="url"
                        value={editForm.portfolio_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, portfolio_url: e.target.value }))}
                        placeholder="https://yourportfolio.com"
                        className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors text-sm"
                        data-testid="portfolio-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#0A0A0A] mb-1.5">
                        <FileText size={12} className="inline mr-1" />
                        Resume URL
                      </label>
                      <input
                        type="url"
                        value={editForm.resume_url || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, resume_url: e.target.value }))}
                        placeholder="https://drive.google.com/..."
                        className="w-full px-4 py-2.5 bg-white border border-[#E4E4E7] rounded-sm text-[#0A0A0A] placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/20 focus:border-[#B91C1C] transition-colors text-sm"
                        data-testid="resume-input"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Projects Tab */}
            {activeTab === 'projects' && (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-[#0A0A0A]">My Projects</h3>
                  <button
                    onClick={() => setShowProjectModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-[#B91C1C] text-white rounded-sm font-medium hover:bg-[#7F1D1D] transition-colors text-sm"
                    data-testid="add-project-btn"
                  >
                    <Plus size={16} />
                    Add Project
                  </button>
                </div>

                {projects.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {projects.map((project, idx) => (
                      <div key={idx} className="bg-white border p-5 transition-all" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="font-semibold text-[#0A0A0A]">{project.title}</h4>
                          <button
                            onClick={() => deleteProject(project.id)}
                            className="p-1.5 text-zinc-400 hover:text-[#DC2626] hover:bg-[#DC2626]/10 rounded-sm transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <p className="text-[#71717A] text-sm mb-3 line-clamp-2">{project.description}</p>
                        <div className="flex flex-wrap gap-1.5 mb-4">
                          {project.tech_stack?.map((tech, tidx) => (
                            <span key={tidx} className="px-2 py-0.5 bg-[#F4F4F5] text-[#71717A] rounded text-xs">
                              {tech}
                            </span>
                          ))}
                        </div>
                        <div className="flex gap-3">
                          {project.github_url && (
                            <a 
                              href={project.github_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm text-[#71717A] hover:text-[#0A0A0A]"
                            >
                              <Github size={14} />
                              GitHub
                            </a>
                          )}
                          {project.live_url && (
                            <a 
                              href={project.live_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-sm text-[#B91C1C] hover:text-[#7F1D1D]"
                            >
                              <ExternalLink size={14} />
                              Live Demo
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border p-12 text-center" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <FolderGit2 className="mx-auto text-zinc-300 mb-4" size={48} />
                    <p className="text-[#71717A] mb-4">No projects yet. Showcase your work!</p>
                    <button
                      onClick={() => setShowProjectModal(true)}
                      className="text-[#B91C1C] hover:underline font-medium"
                    >
                      Add your first project
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Achievements Tab */}
            {activeTab === 'achievements' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">My Achievements</h3>
                {achievements.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {achievements.map((ach, idx) => (
                      <div key={idx} className="bg-white border p-5 transition-all" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-[#D97706]/10 rounded-sm flex items-center justify-center">
                            <Award size={24} className="text-[#D97706]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <h4 className="font-semibold text-[#0A0A0A]">{ach.title}</h4>
                              <span className="text-[#D97706] font-semibold">+{ach.points}</span>
                            </div>
                            <p className="text-[#71717A] text-sm mt-1">{ach.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border p-12 text-center" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <Trophy className="mx-auto text-zinc-300 mb-4" size={48} />
                    <p className="text-[#71717A]">No achievements yet. Complete your profile and solve problems!</p>
                  </div>
                )}
              </div>
            )}

            {/* Submissions Tab */}
            {activeTab === 'submissions' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Submission History</h3>
                {submissions.length > 0 ? (
                  <div className="bg-white border overflow-hidden" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <table className="w-full">
                      <thead className="bg-[#F4F4F5]">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Problem</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Language</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Verdict</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {submissions.map((sub, idx) => (
                          <tr key={idx} className="hover:bg-zinc-50">
                            <td className="px-4 py-3">
                              <Link to={`/codearena/problem/${sub.problem_id}`} className="text-[#B91C1C] hover:underline text-sm font-medium">
                                {sub.problem_title || sub.problem_id}
                              </Link>
                            </td>
                            <td className="px-4 py-3 text-[#71717A] text-sm">{sub.language}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-sm text-xs font-medium ${
                                sub.verdict === 'Accepted' 
                                  ? 'bg-[#059669]/10 text-[#059669]' 
                                  : 'bg-[#b24020]/10 text-[#DC2626]'
                              }`}>
                                {sub.verdict}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[#71717A] text-sm">
                              {new Date(sub.submitted_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-white border p-12 text-center" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <Code className="mx-auto text-zinc-300 mb-4" size={48} />
                    <p className="text-[#71717A]">No submissions yet. Start solving problems!</p>
                    <Link to="/codearena/problems" className="text-[#B91C1C] hover:underline mt-2 inline-block font-medium">
                      Browse Problems
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Courses Tab */}
            {activeTab === 'courses' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">My Courses</h3>
                {myCourses.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {myCourses.map((course, idx) => (
                      <div key={idx} className="bg-white border p-5 transition-all" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                        <div className="flex items-start gap-3">
                          <div className="w-12 h-12 bg-[#B91C1C]/10 rounded-sm flex items-center justify-center">
                            <BookOpen size={24} className="text-[#B91C1C]" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-[#0A0A0A]">{course.title}</h4>
                            <p className="text-[#71717A] text-sm mt-1">{course.description}</p>
                            <div className="mt-3">
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="text-[#71717A]">Progress</span>
                                <span className="text-[#B91C1C] font-medium">{course.progress || 0}%</span>
                              </div>
                              <div className="w-full h-1.5 bg-gray-200 rounded-sm">
                                <div 
                                  className="h-full bg-[#B91C1C] rounded-sm"
                                  style={{ width: `${course.progress || 0}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white border p-12 text-center" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <BookOpen className="mx-auto text-zinc-300 mb-4" size={48} />
                    <p className="text-[#71717A]">No enrolled courses yet.</p>
                    <Link to="/future-skills" className="text-[#B91C1C] hover:underline mt-2 inline-block font-medium">
                      Explore Courses
                    </Link>
                  </div>
                )}
              </div>
            )}

            {/* Purchases Tab */}
            {activeTab === 'purchases' && (
              <div className="space-y-4">
                <h3 className="font-semibold text-[#0A0A0A] mb-4">Purchase History</h3>
                {purchasesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader className="animate-spin text-[#B91C1C]" size={24} />
                  </div>
                ) : purchases.length > 0 ? (
                  <div className="bg-white border overflow-hidden" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <table className="w-full">
                      <thead className="bg-[#F4F4F5]">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Item</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Amount</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Status</th>
                          <th className="px-4 py-3 text-left text-sm font-medium text-[#71717A]">Date</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {purchases.map((purchase, idx) => (
                          <tr key={idx} className="hover:bg-zinc-50">
                            <td className="px-4 py-3">
                              <p className="font-medium text-[#0A0A0A] text-sm">{purchase.item_name || 'Premium Feature'}</p>
                              <p className="text-[#71717A] text-xs">{purchase.description}</p>
                            </td>
                            <td className="px-4 py-3 text-[#0A0A0A] font-medium text-sm">Rs. {purchase.amount}</td>
                            <td className="px-4 py-3">
                              <span className={`px-2.5 py-1 rounded-sm text-xs font-medium ${
                                purchase.status === 'completed' 
                                  ? 'bg-[#059669]/10 text-[#059669]' 
                                  : purchase.status === 'pending'
                                  ? 'bg-[#D97706]/10 text-[#D97706]'
                                  : 'bg-[#b24020]/10 text-[#DC2626]'
                              }`}>
                                {purchase.status}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-[#71717A] text-sm">
                              {new Date(purchase.created_at).toLocaleDateString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="bg-white border p-12 text-center" style={{ borderColor: "#E4E4E7", borderRadius: "2px" }}>
                    <FileText className="mx-auto text-zinc-300 mb-4" size={48} />
                    <p className="text-[#71717A]">No purchases yet.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Project Modal */}
      {showProjectModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white border w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ borderColor: '#E4E4E7', borderRadius: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: '#E4E4E7' }}>
              <h3 className="text-base font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>Add New Project</h3>
              <button 
                onClick={() => setShowProjectModal(false)}
                className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors"
                style={{ borderRadius: '2px' }}
              >
                <X size={18} />
              </button>
            </div>
            
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>Project Title*</label>
                <input
                  type="text"
                  value={projectForm.title}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, title: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border text-sm focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder="My Awesome Project"
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>Description*</label>
                <textarea
                  value={projectForm.description}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-2.5 bg-white border text-sm focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C] resize-none"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder="Brief description of your project..."
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>Tech Stack</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {projectForm.tech_stack.map((tech, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-2.5 py-1 text-sm" style={{ background: '#B91C1C10', color: '#B91C1C', borderRadius: '1px' }}>
                      {tech}
                      <button onClick={() => setProjectForm(prev => ({ ...prev, tech_stack: prev.tech_stack.filter((_, i) => i !== idx) }))}>
                        <X size={12} />
                      </button>
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTech}
                    onChange={(e) => setNewTech(e.target.value)}
                    className="flex-1 px-4 py-2 bg-white border text-sm focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
                    style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                    placeholder="React, Node.js..."
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (newTech.trim()) {
                          setProjectForm(prev => ({ ...prev, tech_stack: [...prev.tech_stack, newTech.trim()] }));
                          setNewTech('');
                        }
                      }
                    }}
                  />
                  <button
                    onClick={() => {
                      if (newTech.trim()) {
                        setProjectForm(prev => ({ ...prev, tech_stack: [...prev.tech_stack, newTech.trim()] }));
                        setNewTech('');
                      }
                    }}
                    className="px-3 py-2 transition-colors" style={{ background: '#B91C1C10', color: '#B91C1C', borderRadius: '2px' }}
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>GitHub URL</label>
                <input
                  type="url"
                  value={projectForm.github_url}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, github_url: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border text-sm focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder="https://github.com/..."
                />
              </div>
              
              <div>
                <label className="block text-[10px] font-bold tracking-[0.1em] uppercase mb-2" style={{ color: '#A1A1AA' }}>Live Demo URL</label>
                <input
                  type="url"
                  value={projectForm.live_url}
                  onChange={(e) => setProjectForm(prev => ({ ...prev, live_url: e.target.value }))}
                  className="w-full px-4 py-2.5 bg-white border text-sm focus:outline-none focus:ring-1 focus:ring-[#B91C1C] focus:border-[#B91C1C]"
                  style={{ borderColor: '#E4E4E7', borderRadius: '2px', color: '#0A0A0A' }}
                  placeholder="https://myproject.com"
                />
              </div>
            </div>
            
            <div className="flex gap-3 p-5 border-t" style={{ borderColor: '#E4E4E7' }}>
              <button
                onClick={() => setShowProjectModal(false)}
                className="flex-1 px-4 py-2.5 border font-medium hover:bg-zinc-50 transition-colors text-sm"
                style={{ borderColor: '#E4E4E7', color: '#71717A', borderRadius: '2px' }}
              >
                Cancel
              </button>
              <button
                onClick={addProject}
                className="flex-1 px-4 py-2.5 text-white font-bold text-sm transition-colors"
                style={{ background: '#B91C1C', borderRadius: '2px' }}
              >
                Add Project
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Upgrade Popup */}
      {showUpgradePopup && (
        <ResumeUpgradePopup onClose={() => setShowUpgradePopup(false)} />
      )}
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// VerifiedShareCard — one-click sharing for the EvalentX Verified public profile
// ──────────────────────────────────────────────────────────────────────────
const VerifiedShareCard = ({ username, fullName, badgeExpires, planCode }) => {
  // Always anchor to the public EvalentX domain so links posted to social
  // platforms never leak preview/internal hostnames.
  const publicOrigin = getFrontendUrl();
  const profileUrl = `${publicOrigin}/profile/${username}`;
  // OG-aware share URL: backend serves crawler-friendly meta + redirects humans
  // to /profile/{username}. Posting this on LinkedIn/WhatsApp/Twitter renders
  // a polished verified-badge card with the candidate's score + tier.
  const shareUrl = `${publicOrigin}/api/share/profile/${encodeURIComponent(username)}`;
  const shareText = `I just got EvalentX Verified${planCode ? ` (${planCode.toUpperCase()})` : ''}. View my verified profile:`;
  const encUrl = encodeURIComponent(shareUrl);
  const encText = encodeURIComponent(shareText);

  const linkedinHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encUrl}`;
  const twitterHref  = `https://twitter.com/intent/tweet?text=${encText}&url=${encUrl}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
  const emailHref    = `mailto:?subject=${encodeURIComponent('My EvalentX Verified Profile')}&body=${encodeURIComponent(`${shareText}\n\n${shareUrl}`)}`;

  const expires = badgeExpires ? new Date(badgeExpires).toLocaleDateString() : null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success('Profile link copied — share preview will show your verified badge');
    } catch {
      toast.error('Could not copy');
    }
  };

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'EvalentX Verified', text: shareText, url: shareUrl }); } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <div
      className="bg-white border p-5 sm:p-6 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 lg:gap-8 items-start lg:items-center"
      style={{ borderColor: '#E5E5E5', borderRadius: '2px', background: 'linear-gradient(180deg, #FFFFFF 0%, #FFFBEB 100%)' }}
      data-testid="verified-share-card"
    >
      <div className="min-w-0">
        <p className="text-[10px] font-bold tracking-[0.2em] uppercase mb-1" style={{ color: '#B45309' }}>
          ★ EvalentX Verified
        </p>
        <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight" style={{ fontFamily: "'Space Grotesk', sans-serif", color: '#0A0A0A' }}>
          Your verified profile is live{fullName ? `, ${fullName.split(' ')[0]}` : ''}.
        </h2>
        <p className="text-sm mt-1.5" style={{ color: '#525252' }}>
          Push your verified badge in front of recruiters in one click.
          {expires && <span className="text-[#A3A3A3]"> Valid until {expires}.</span>}
        </p>

        {/* URL row */}
        <div
          className="mt-4 flex items-center gap-2 px-3 py-2 border bg-white"
          style={{ borderColor: '#E5E5E5', borderRadius: '2px' }}
        >
          <span className="font-mono text-xs sm:text-sm truncate flex-1" style={{ color: '#0A0A0A' }} data-testid="verified-share-url">
            {profileUrl}
          </span>
          <button
            type="button"
            onClick={copyLink}
            className="px-3 py-1 text-xs font-bold tracking-wide uppercase border transition-colors hover:bg-black hover:text-white shrink-0"
            style={{ borderColor: '#0A0A0A', color: '#0A0A0A', borderRadius: '2px' }}
            data-testid="verified-share-copy-btn"
          >
            Copy
          </button>
        </div>

        {/* Quick share row */}
        <div className="mt-3 flex flex-wrap gap-2" data-testid="verified-share-quick-row">
          <SocialChip href={linkedinHref} bg="#0A66C2" testid="share-linkedin">in · LinkedIn</SocialChip>
          <SocialChip href={twitterHref}  bg="#0A0A0A" testid="share-twitter">𝕏 · Post</SocialChip>
          <SocialChip href={whatsappHref} bg="#25D366" testid="share-whatsapp">WhatsApp</SocialChip>
          <SocialChip href={emailHref}    bg="#525252" testid="share-email">Email</SocialChip>
          <button
            type="button"
            onClick={nativeShare}
            className="px-3 py-1.5 text-xs font-bold tracking-wide uppercase border bg-white hover:bg-[#FAFAF9] transition-colors"
            style={{ borderColor: '#0A0A0A', color: '#0A0A0A', borderRadius: '2px' }}
            data-testid="share-native"
          >
            Share…
          </button>
        </div>
      </div>

      <Link
        to={`/profile/${username}`}
        className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold text-sm tracking-wide uppercase whitespace-nowrap"
        style={{ background: '#0A0A0A', borderRadius: '2px' }}
        data-testid="verified-share-open-profile"
      >
        Open public profile
        <ArrowRight size={14} />
      </Link>
    </div>
  );
};

const SocialChip = ({ href, bg, children, testid }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="px-3 py-1.5 text-xs font-bold tracking-wide uppercase text-white transition-transform hover:-translate-y-0.5"
    style={{ background: bg, borderRadius: '2px' }}
    data-testid={testid}
  >
    {children}
  </a>
);

export default CodeArenaDashboardPage;
